import type { BoxProps } from '@everlywell/ui-kit';

type SX = NonNullable<BoxProps['sx']>;

export type PlyrState =
  | 'root'
  | 'video'
  | 'audio'
  | 'fullUi'
  | 'fullscreen'
  | 'pip'
  | 'airplay'
  | 'captionsActive'
  | 'controlsHidden'
  | 'stopped'
  | 'paused'
  | 'playing'
  | 'buffering'
  | 'seeking'
  | 'loading'
  | 'error';

export type VideoSxShortcuts = {
  root?: SX;

  player?: {
    base?: SX;
    video?: SX;
    audio?: SX;
    fullUi?: SX;
  };

  layers?: {
    videoWrapper?: SX;
    poster?: SX;
    captions?: {
      container?: SX;
      text?: SX;
    };
    srOnly?: SX;
  };

  playLarge?: {
    button?: SX;
    hover?: SX;
    focus?: SX;
    icon?: SX;
  };

  controls?: {
    bar?: SX;
    barHover?: SX;
    groupItem?: SX;
    groupItemHidden?: SX;

    control?: SX;
    controlHover?: SX;
    controlFocus?: SX;
    controlPressed?: SX;
    controlDisabled?: SX;
    controlIcon?: SX;
    controlLabel?: SX;
  };

  progress?: {
    container?: SX;
    input?: SX;
    rail?: SX;
    buffered?: SX;
    playedTrack?: SX;
    thumb?: SX;
  };

  time?: {
    container?: SX;
    current?: SX;
    duration?: SX;
    delimiter?: SX;
  };

  volume?: {
    container?: SX;
    input?: SX;
    thumb?: SX; // range thumb
  };

  tooltip?: {
    base?: SX;
    shown?: SX;
  };

  menu?: {
    button?: SX;
    container?: SX;
    panel?: SX;
    item?: SX;
    itemHover?: SX;
    itemActive?: SX;
    value?: SX;
  };

  preview?: {
    thumb?: SX;
    scrubbing?: SX;
    marker?: SX;
  };

  state?: Partial<Record<PlyrState, SX>>;
};

type SxBuilder = Record<string, SX | undefined>;

function merge(base: SxBuilder, key: string, value?: SX): SxBuilder {
  return value ? { ...base, [key]: value } : base;
}

function mergeState(base: SxBuilder, key: string, value?: SX): SxBuilder {
  if (!value) return base;
  return {
    ...base,
    [key]: {
      ...((base[key] as SX) || {}),
      ...value,
    },
  };
}

function withRangeThumb(
  base: SxBuilder,
  selector: string,
  thumb?: SX,
): SxBuilder {
  if (!thumb) return base;
  return {
    ...base,
    [`${selector}::-webkit-slider-thumb`]: thumb,
    [`${selector}::-moz-range-thumb`]: thumb,
    [`${selector}::-ms-thumb`]: thumb,
  };
}

function buildBaseSelectors(
  shortcuts: VideoSxShortcuts,
  root: SxBuilder,
): SxBuilder {
  const selectors: Array<[SX | undefined, string]> = [
    // Player root/variants
    [shortcuts.player?.base, '& .plyr'],
    [shortcuts.player?.video, '& .plyr.plyr--video'],
    [shortcuts.player?.audio, '& .plyr.plyr--audio'],
    [shortcuts.player?.fullUi, '& .plyr.plyr--full-ui'],

    // Layers
    [shortcuts.layers?.videoWrapper, '& .plyr .plyr__video-wrapper'],
    [shortcuts.layers?.poster, '& .plyr .plyr__poster'],
    [shortcuts.layers?.captions?.container, '& .plyr .plyr__captions'],
    [
      shortcuts.layers?.captions?.text,
      '& .plyr .plyr__captions .plyr__caption',
    ],
    [shortcuts.layers?.srOnly, '& .plyr .plyr__sr-only'],

    // Big play overlay
    [shortcuts.playLarge?.button, '& .plyr .plyr__control--overlaid'],
    [shortcuts.playLarge?.hover, '& .plyr .plyr__control--overlaid:hover'],
    [
      shortcuts.playLarge?.focus,
      '& .plyr .plyr__control--overlaid:focus-visible',
    ],
    [shortcuts.playLarge?.icon, '& .plyr .plyr__control--overlaid svg'],

    // Controls
    [shortcuts.controls?.bar, '& .plyr .plyr__controls'],
    [shortcuts.controls?.barHover, '& .plyr .plyr__controls:hover'],
    [
      shortcuts.controls?.groupItem,
      '& .plyr .plyr__controls .plyr__controls__item',
    ],
    [
      shortcuts.controls?.groupItemHidden,
      '& .plyr .plyr__controls .plyr__controls__item.plyr__controls__item--hidden',
    ],
    [shortcuts.controls?.control, '& .plyr .plyr__control'],
    [shortcuts.controls?.controlHover, '& .plyr .plyr__control:hover'],
    [shortcuts.controls?.controlFocus, '& .plyr .plyr__control:focus-visible'],
    [
      shortcuts.controls?.controlPressed,
      '& .plyr .plyr__control[aria-pressed="true"]',
    ],
    [
      shortcuts.controls?.controlDisabled,
      '& .plyr .plyr__control[disabled], & .plyr .plyr__control[aria-disabled="true"]',
    ],
    [shortcuts.controls?.controlIcon, '& .plyr .plyr__control svg'],

    // Progress
    [shortcuts.progress?.container, '& .plyr .plyr__progress'],
    [shortcuts.progress?.input, '& .plyr .plyr__progress input[type="range"]'],
    [shortcuts.progress?.buffered, '& .plyr .plyr__progress__buffer'],

    // Time
    [shortcuts.time?.container, '& .plyr .plyr__time'],
    [shortcuts.time?.current, '& .plyr .plyr__time.plyr__time--current'],
    [shortcuts.time?.duration, '& .plyr .plyr__time.plyr__time--duration'],

    // Volume
    [shortcuts.volume?.container, '& .plyr .plyr__volume'],
    [shortcuts.volume?.input, '& .plyr .plyr__volume input[type="range"]'],

    // Tooltips
    [shortcuts.tooltip?.base, '& .plyr .plyr__tooltip'],
    [shortcuts.tooltip?.shown, '& .plyr .plyr__tooltip.plyr__tooltip--visible'],

    // Menus
    [shortcuts.menu?.container, '& .plyr .plyr__menu__container'],
    [shortcuts.menu?.panel, "& .plyr .plyr__menu__container [role='menu']"],
    [
      shortcuts.menu?.item,
      "& .plyr .plyr__menu__container [role='menuitemradio']",
    ],
    [
      shortcuts.menu?.itemHover,
      "& .plyr .plyr__menu__container [role='menuitemradio']:hover",
    ],
    [
      shortcuts.menu?.itemActive,
      "& .plyr .plyr__menu__container [role='menuitemradio'][aria-checked='true']",
    ],

    // Preview
    [shortcuts.preview?.thumb, '& .plyr .plyr__preview-thumb'],
    [shortcuts.preview?.scrubbing, '& .plyr .plyr__preview-scrubbing'],
    [shortcuts.preview?.marker, '& .plyr .plyr__marker'],
  ];

  return selectors.reduce((acc, [value, sel]) => merge(acc, sel, value), root);
}

function addRangeThumbs(
  base: SxBuilder,
  shortcuts: VideoSxShortcuts,
): SxBuilder {
  const withProgress = withRangeThumb(
    base,
    '& .plyr .plyr__progress input[type="range"]',
    shortcuts.progress?.thumb,
  );

  return withRangeThumb(
    withProgress,
    '& .plyr .plyr__volume input[type="range"]',
    shortcuts.volume?.thumb,
  );
}

function mergeStateVariants(
  base: SxBuilder,
  shortcuts: VideoSxShortcuts,
): SxBuilder {
  const state = shortcuts.state;
  if (!state) return base;

  const variants: Array<[PlyrState, string]> = [
    ['video', '& .plyr.plyr--video'],
    ['audio', '& .plyr.plyr--audio'],
    ['fullUi', '& .plyr.plyr--full-ui'],
  ];

  return variants.reduce(
    (acc, [key, selector]) => mergeState(acc, selector, state[key]),
    base,
  );
}

function addStateModifiers(
  base: SxBuilder,
  shortcuts: VideoSxShortcuts,
): SxBuilder {
  const state = shortcuts.state;
  if (!state) return base;

  const modifiers: Array<[PlyrState, string]> = [
    ['fullscreen', '& .plyr.plyr--fullscreen-enabled'],
    ['pip', '& .plyr.plyr--pip-enabled'],
    ['airplay', '& .plyr.plyr--airplay-active'],
    ['captionsActive', '& .plyr.plyr--captions-active'],
    ['controlsHidden', '& .plyr.plyr--hide-controls'],
    ['stopped', '& .plyr.plyr--stopped'],
    ['paused', '& .plyr.plyr--paused'],
    ['playing', '& .plyr.plyr--playing'],
    ['buffering', '& .plyr.plyr--buffering'],
    ['seeking', '& .plyr.plyr--seeking'],
    ['loading', '& .plyr.plyr--loading'],
    ['error', '& .plyr.plyr--error'],
  ];

  return modifiers.reduce(
    (acc, [key, selector]) => merge(acc, selector, state[key]),
    base,
  );
}

export function themeBuilder(s?: VideoSxShortcuts): SX {
  if (!s) return {};

  const root: SxBuilder = (s.root ?? {}) as SxBuilder;
  const base = buildBaseSelectors(s, root);
  const withThumbs = addRangeThumbs(base, s);
  const withVariants = mergeStateVariants(withThumbs, s);
  const final = addStateModifiers(withVariants, s);

  return final as SX;
}
